/**
 * 自动测试ID插件
 * 自动为所有DOM元素添加 data-testid、data-static、focusable 等属性
 * 用于自动化测试和无障碍访问
 */

import type { App } from 'vue'

/**
 * 为元素添加测试属性
 * @param element HTML元素
 * @param testId 测试ID
 */
function addTestAttributes(element: HTMLElement, testId: string) {
  // 如果元素已经有 data-testid，跳过
  if (element.hasAttribute('data-testid')) {
    return
  }

  // 添加 data-testid
  element.setAttribute('data-testid', testId)

  // 添加 data-static (百度风格)
  element.setAttribute('data-static', testId)

  // 检查是否应该添加 focusable 属性
  const tagName = element.tagName
  const naturallyFocusable = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].includes(tagName)
  const hasTabindex = element.hasAttribute('tabindex')
  const hasClickHandler = element.onclick !== null || element.hasAttribute('role')

  if (naturallyFocusable || hasTabindex || hasClickHandler) {
    element.setAttribute('focusable', 'true')

    // 如果不是原生可聚焦元素且没有 tabindex，添加 tabindex
    if (!naturallyFocusable && !hasTabindex) {
      element.setAttribute('tabindex', '0')
    }
  }

  // 如果没有 aria-label，添加一个
  if (!element.hasAttribute('aria-label') && !element.hasAttribute('aria-labelledby')) {
    const ariaLabel = testId
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
    element.setAttribute('aria-label', ariaLabel)
  }
}

/**
 * 遍历并处理所有元素
 * @param container 容器元素
 */
function processElements(container: HTMLElement) {
  const elements = container.querySelectorAll('*')

  elements.forEach((element, index) => {
    const htmlElement = element as HTMLElement

    // 跳过 script、style 等标签
    if (['SCRIPT', 'STYLE', 'META', 'TITLE'].includes(htmlElement.tagName)) {
      return
    }

    // 生成测试ID
    const tagName = htmlElement.tagName.toLowerCase()
    const classList = htmlElement.className
    const classHint = typeof classList === 'string' && classList
      ? `-${classList.split(' ')[0]}`
      : ''
    const testId = `${tagName}${classHint}-${index}`

    // 添加属性
    addTestAttributes(htmlElement, testId)
  })
}

/**
 * 自动测试ID插件配置
 */
export interface AutoTestIdOptions {
  /** 是否启用（默认true） */
  enabled?: boolean
  /** 是否在生产环境启用（默认false） */
  enableInProduction?: boolean
  /** 是否启用详细日志 */
  verbose?: boolean
}

/**
 * 自动测试ID插件
 */
export default {
  install(app: App, options: AutoTestIdOptions = {}) {
    const {
      enabled = true,
      enableInProduction = false,
      verbose = false
    } = options

    // 检查是否应该启用
    const isProduction = process.env.NODE_ENV === 'production'
    const shouldEnable = enabled && (!isProduction || enableInProduction)

    if (!shouldEnable) {
      if (verbose) {
        console.log('[AutoTestId] 插件已禁用')
      }
      return
    }

    if (verbose) {
      console.log('[AutoTestId] 插件已启用')
    }

    // 在应用挂载后处理
    app.mixin({
      mounted() {
        this.$nextTick(() => {
          const element = this.$el as HTMLElement
          if (element && element.nodeType === Node.ELEMENT_NODE) {
            processElements(element)
          }
        })
      },
      updated() {
        this.$nextTick(() => {
          const element = this.$el as HTMLElement
          if (element && element.nodeType === Node.ELEMENT_NODE) {
            processElements(element)
          }
        })
      }
    })

    if (verbose) {
      console.log('[AutoTestId] 自动测试ID系统已激活')
    }
  }
}
